import { type ToolCall as AgUiToolCall } from '@ag-ui/client'
import { type CopilotSearchSourcePage } from '@precis-digital/kurama/MessageLayout'
import { type CopilotThinkingTitles, type CopilotToolDefinition } from './toolRegistry/types'
import { isRecord, parseToolCallArgumentsForDisplay } from './toolRegistry/parsing'
import { groupKnowledgeSearchSources } from './utils/groupKnowledgeSearchSources'

const parseQueryArg = (toolCall: AgUiToolCall, key = 'query'): string => {
  try {
    const args = JSON.parse(toolCall.function.arguments) as Record<string, unknown>
    const query = args[key]
    return typeof query === 'string' && query.trim().length > 0 ? query : 'unknown query'
  } catch {
    return 'unknown query'
  }
}

const parseDimensionDescription = (toolCall: AgUiToolCall): string => {
  const parsedArgs = parseToolCallArgumentsForDisplay(toolCall.function.arguments)
  if (!isRecord(parsedArgs)) {
    return 'dimension values'
  }

  const dimension = parsedArgs.dimension
  return typeof dimension === 'string' && dimension.trim().length > 0 ? `${dimension} values` : 'dimension values'
}

const parseSkillNameDescription = (toolCall: AgUiToolCall): string => {
  const parsedArgs = parseToolCallArgumentsForDisplay(toolCall.function.arguments)
  if (!isRecord(parsedArgs)) {
    return 'skill'
  }

  const skillName = parsedArgs.skill_name
  return typeof skillName === 'string' && skillName.trim().length > 0 ? skillName : 'skill'
}

const parseSkillResourceDescription = (toolCall: AgUiToolCall): string => {
  const parsedArgs = parseToolCallArgumentsForDisplay(toolCall.function.arguments)
  if (!isRecord(parsedArgs)) {
    return 'skill resource'
  }

  const skillName = typeof parsedArgs.skill_name === 'string' ? parsedArgs.skill_name.trim() : ''
  const filePath = typeof parsedArgs.file_path === 'string' ? parsedArgs.file_path.trim() : ''

  if (skillName.length > 0 && filePath.length > 0) {
    return `${skillName}: ${filePath}`
  }
  if (filePath.length > 0) {
    return filePath
  }
  if (skillName.length > 0) {
    return skillName
  }
  return 'skill resource'
}

/**
 * Google Ads, Meta, and Merchant Center all expose `get_account_info` under the
 * same tool name. Discriminate on the id arg the agent actually sent.
 */
export const resolveSharedAccountInfoTitles = (toolCall: AgUiToolCall): CopilotThinkingTitles => {
  const parsedArgs = parseToolCallArgumentsForDisplay(toolCall.function.arguments)
  if (!isRecord(parsedArgs)) {
    return { pastTense: 'Checked account', presentTense: 'Checking account' }
  }
  if (typeof parsedArgs.customer_id === 'string' && parsedArgs.customer_id.trim().length > 0) {
    return { pastTense: 'Checked Google Ads account', presentTense: 'Checking Google Ads account' }
  }
  if (typeof parsedArgs.merchant_id === 'string' && parsedArgs.merchant_id.trim().length > 0) {
    return { pastTense: 'Checked Merchant Center account', presentTense: 'Checking Merchant Center account' }
  }
  if (typeof parsedArgs.account_id === 'string' && parsedArgs.account_id.trim().length > 0) {
    return { pastTense: 'Checked Meta Ads account', presentTense: 'Checking Meta Ads account' }
  }
  return { pastTense: 'Checked account', presentTense: 'Checking account' }
}

export const resolveSharedAccountInfoDescription = (toolCall: AgUiToolCall): string => {
  const titles = resolveSharedAccountInfoTitles(toolCall)
  if (titles.pastTense.includes('Google Ads')) return 'Google Ads account details'
  if (titles.pastTense.includes('Merchant Center')) return 'Merchant Center account details'
  if (titles.pastTense.includes('Meta Ads')) return 'Meta Ads account details'
  return 'account details'
}

/**
 * Google Ads and Meta both expose `list_campaigns`. Discriminate on id args.
 */
export const resolveSharedListCampaignsTitles = (toolCall: AgUiToolCall): CopilotThinkingTitles => {
  const parsedArgs = parseToolCallArgumentsForDisplay(toolCall.function.arguments)
  if (!isRecord(parsedArgs)) {
    return { pastTense: 'Listed campaigns', presentTense: 'Listing campaigns' }
  }
  if (typeof parsedArgs.customer_id === 'string' && parsedArgs.customer_id.trim().length > 0) {
    return { pastTense: 'Listed Google Ads campaigns', presentTense: 'Listing Google Ads campaigns' }
  }
  if (typeof parsedArgs.account_id === 'string' && parsedArgs.account_id.trim().length > 0) {
    return { pastTense: 'Listed Meta campaigns', presentTense: 'Listing Meta campaigns' }
  }
  return { pastTense: 'Listed campaigns', presentTense: 'Listing campaigns' }
}

export const resolveSharedListCampaignsDescription = (toolCall: AgUiToolCall): string => {
  const titles = resolveSharedListCampaignsTitles(toolCall)
  if (titles.pastTense.includes('Google Ads')) return 'Google Ads campaigns'
  if (titles.pastTense.includes('Meta')) return 'Meta campaigns'
  return 'campaigns'
}

/**
 * User-facing name for a Cirrus-backed report. "Cirrus" is an internal system name —
 * never show it in the transcript. Map the `report` arg (collection is session state,
 * not on the tool call) onto the Alvie product the user already knows.
 */
export const resolveAlvieDataSourceLabel = (report: unknown): string => {
  if (typeof report !== 'string' || report.trim().length === 0) {
    return 'Alvie Data'
  }
  const key = report.trim()

  if (key.startsWith('attribution')) return 'Alvie Attribution'
  if (key.startsWith('bo_')) return 'Budget Optimiser'
  if (key === 'event_feed') return 'Recommendations'
  if (
    key === 'transformed_events' ||
    key === 'profit' ||
    key === 'config_errors' ||
    key.startsWith('events_statuses')
  ) {
    return 'Bidding Signals'
  }
  if (key.startsWith('creative_insights')) return 'Creative Insights'
  if (key.startsWith('search_synergies')) return 'Search Synergies'
  if (key.startsWith('pmm_')) return 'Product Mix'
  if (key.startsWith('arima_plus')) return 'Forecast'
  if (key.startsWith('reporting_base') || key.startsWith('channel_grouping')) {
    return 'Reporting Solution'
  }
  return 'Alvie Data'
}

const parseReportArg = (toolCall: AgUiToolCall): unknown => {
  const parsedArgs = parseToolCallArgumentsForDisplay(toolCall.function.arguments)
  return isRecord(parsedArgs) ? parsedArgs.report : undefined
}

export const resolveCirrusInspectTitles = (toolCall: AgUiToolCall): CopilotThinkingTitles => {
  const product = resolveAlvieDataSourceLabel(parseReportArg(toolCall))
  if (toolCall.function.name === 'list_cirrus_dimension_values') {
    return {
      pastTense: `Listed ${product} filter values`,
      presentTense: `Listing ${product} filter values`
    }
  }
  return {
    pastTense: `Listed ${product} fields`,
    presentTense: `Listing ${product} fields`
  }
}

export const resolveCirrusInspectDescription = (toolCall: AgUiToolCall): string => {
  const product = resolveAlvieDataSourceLabel(parseReportArg(toolCall))
  if (toolCall.function.name === 'list_cirrus_dimension_values') return `${product} filter values`
  return `${product} metrics and dimensions`
}

export const resolveCirrusFetchLabels = (
  args: Record<string, unknown> | undefined
): { actionLabel: string; thinkingTitles: CopilotThinkingTitles } => {
  const product = resolveAlvieDataSourceLabel(args?.report)
  return {
    actionLabel: `Fetched ${product}:`,
    thinkingTitles: {
      pastTense: `Fetched ${product}`,
      presentTense: `Fetching ${product}`
    }
  }
}

const inspectSource = (
  pastTense: string,
  presentTense: string,
  description: string,
  getDescription?: (toolCall: AgUiToolCall) => string,
  getThinkingTitles?: (toolCall: AgUiToolCall) => CopilotThinkingTitles
): CopilotToolDefinition => ({
  presentation: {
    kind: 'thinking_inspect_source',
    thinkingTitles: { pastTense, presentTense },
    description,
    ...(getDescription != null ? { getDescription } : {}),
    ...(getThinkingTitles != null ? { getThinkingTitles } : {})
  }
})

const dataSearch = (
  pastTense: string,
  presentTense: string,
  getSearchTerm: (toolCall: AgUiToolCall) => string,
  getSearchDetails?: (
    toolCall: AgUiToolCall,
    toolResultContent: string | undefined
  ) => CopilotSearchSourcePage[] | undefined
): CopilotToolDefinition => ({
  presentation: {
    kind: 'thinking_search',
    thinkingTitles: { pastTense, presentTense },
    getSearchTerm,
    ...(getSearchDetails != null ? { getSearchDetails } : {})
  }
})

const dataFetch = (reportLabel: string): CopilotToolDefinition => ({
  presentation: {
    kind: 'action_data_fetch',
    actionLabel: `Fetched ${reportLabel}:`,
    thinkingTitles: {
      pastTense: `Fetched ${reportLabel}`,
      presentTense: `Fetching ${reportLabel}`
    }
  }
})

const dataAnalysis = (analysisLabel: string): CopilotToolDefinition => ({
  presentation: {
    kind: 'action_data_analysis',
    actionLabel: `Analysed ${analysisLabel}:`,
    thinkingTitles: {
      pastTense: `Analysed ${analysisLabel}`,
      presentTense: `Analysing ${analysisLabel}`
    }
  }
})

/** Shared entry for tools that must stay silent in the Alvie transcript. */
const HIDDEN: CopilotToolDefinition = {
  presentation: { kind: 'hidden' }
}

/**
 * Registry entries for precis_agent_v1 data-agent tools.
 *
 * Every tool the agent can emit should appear here as either a real presentation
 * or `kind: 'hidden'`. Omitting a tool triggers a console warning when it shows
 * up in a chat trace — use that to catch new tools and wire them explicitly.
 * Dynamic MCP tool names from FlowMcpToolset are intentionally left unmapped.
 */
export const DATA_AGENT_TOOL_REGISTRY: Record<string, CopilotToolDefinition> = {
  get_account_info: inspectSource(
    'Checked account',
    'Checking account',
    'account details',
    resolveSharedAccountInfoDescription,
    resolveSharedAccountInfoTitles
  ),
  get_property_info: inspectSource('Checked GA4 property', 'Checking GA4 property', 'property details'),
  get_site_info: inspectSource('Checked Search Console site', 'Checking Search Console site', 'site details'),
  list_campaigns: inspectSource(
    'Listed campaigns',
    'Listing campaigns',
    'campaigns',
    resolveSharedListCampaignsDescription,
    resolveSharedListCampaignsTitles
  ),
  list_ad_groups: inspectSource('Listed ad groups', 'Listing ad groups', 'Google Ads ad groups'),
  list_conversion_actions: inspectSource(
    'Listed conversion actions',
    'Listing conversion actions',
    'conversion actions'
  ),
  list_dimensions_and_metrics: inspectSource(
    'Listed available fields',
    'Listing available fields',
    'GA4 dimensions and metrics'
  ),
  list_dimension_values: inspectSource(
    'Listed dimension values',
    'Listing dimension values',
    'dimension values',
    parseDimensionDescription
  ),
  list_active_events: inspectSource('Listed events', 'Listing events', 'GA4 events'),
  list_sitemaps: inspectSource('Listed sitemaps', 'Listing sitemaps', 'Search Console sitemaps'),
  list_available_reports: inspectSource('Listed reports', 'Listing reports', 'starter report types'),
  list_available_dimensions_and_metrics_for_report: inspectSource(
    'Listed report fields',
    'Listing report fields',
    'report schema'
  ),
  list_dimension_values_cross_channel: inspectSource(
    'Listed filter values',
    'Listing filter values',
    'cross-channel dimensions'
  ),
  list_dimension_values_all_marketing_data: inspectSource(
    'Listed filter values',
    'Listing filter values',
    'marketing data dimensions'
  ),
  list_sources_of_knowledge: inspectSource(
    'Listed knowledge sources',
    'Listing knowledge sources',
    'connected knowledge sources'
  ),
  list_skills: inspectSource('Listed skills', 'Listing skills', 'workspace skills'),
  load_skill: inspectSource('Loaded skill', 'Loading skill', 'skill', parseSkillNameDescription),
  load_skill_resource: inspectSource(
    'Loaded skill resource',
    'Loading skill resource',
    'skill resource',
    parseSkillResourceDescription
  ),
  // Session cache lookup — agent reuses prior fetches; not user-facing.
  list_saved_reports: HIDDEN,

  // --- GA4 admin ---
  get_ga4_admin_metadata: inspectSource(
    'Checked GA4 admin settings',
    'Checking GA4 admin settings',
    'property admin metadata'
  ),

  // --- Meta discovery ---
  list_ad_sets: inspectSource('Listed ad sets', 'Listing ad sets', 'Meta ad sets'),
  list_ads: inspectSource('Listed ads', 'Listing ads', 'Meta ads'),
  list_meta_fields: inspectSource('Listed Meta fields', 'Listing Meta fields', 'Meta Ads field catalog'),
  list_breakdown_values: inspectSource('Listed breakdown values', 'Listing breakdown values', 'Meta breakdown values'),
  list_actions: inspectSource('Listed action types', 'Listing action types', 'Meta action types'),

  // --- LinkedIn ---
  get_linkedin_account_info: inspectSource(
    'Checked LinkedIn account',
    'Checking LinkedIn account',
    'LinkedIn account details'
  ),
  list_linkedin_campaign_groups: inspectSource(
    'Listed campaign groups',
    'Listing campaign groups',
    'LinkedIn campaign groups'
  ),
  list_linkedin_campaigns: inspectSource(
    'Listed LinkedIn campaigns',
    'Listing LinkedIn campaigns',
    'LinkedIn campaigns'
  ),
  list_linkedin_creatives: inspectSource('Listed creatives', 'Listing creatives', 'LinkedIn creatives'),
  list_linkedin_fields: inspectSource('Listed LinkedIn fields', 'Listing LinkedIn fields', 'LinkedIn field catalog'),

  // --- TikTok ---
  get_tiktok_account_info: inspectSource('Checked TikTok account', 'Checking TikTok account', 'TikTok account details'),
  list_tiktok_campaigns: inspectSource('Listed TikTok campaigns', 'Listing TikTok campaigns', 'TikTok campaigns'),
  list_tiktok_adgroups: inspectSource('Listed TikTok ad groups', 'Listing TikTok ad groups', 'TikTok ad groups'),
  list_tiktok_ads: inspectSource('Listed TikTok ads', 'Listing TikTok ads', 'TikTok ads'),
  list_tiktok_fields: inspectSource('Listed TikTok fields', 'Listing TikTok fields', 'TikTok field catalog'),

  // --- Snapchat ---
  get_snapchat_account_info: inspectSource(
    'Checked Snapchat account',
    'Checking Snapchat account',
    'Snapchat account details'
  ),
  list_snapchat_campaigns: inspectSource(
    'Listed Snapchat campaigns',
    'Listing Snapchat campaigns',
    'Snapchat campaigns'
  ),
  list_snapchat_adsquads: inspectSource('Listed ad squads', 'Listing ad squads', 'Snapchat ad squads'),
  list_snapchat_ads: inspectSource('Listed Snapchat ads', 'Listing Snapchat ads', 'Snapchat ads'),
  list_snapchat_fields: inspectSource('Listed Snapchat fields', 'Listing Snapchat fields', 'Snapchat field catalog'),

  // --- Pinterest ---
  get_pinterest_account_info: inspectSource(
    'Checked Pinterest account',
    'Checking Pinterest account',
    'Pinterest account details'
  ),
  list_pinterest_campaigns: inspectSource(
    'Listed Pinterest campaigns',
    'Listing Pinterest campaigns',
    'Pinterest campaigns'
  ),
  list_pinterest_ad_groups: inspectSource(
    'Listed Pinterest ad groups',
    'Listing Pinterest ad groups',
    'Pinterest ad groups'
  ),
  list_pinterest_ads: inspectSource('Listed Pinterest ads', 'Listing Pinterest ads', 'Pinterest ads'),
  list_pinterest_fields: inspectSource(
    'Listed Pinterest fields',
    'Listing Pinterest fields',
    'Pinterest field catalog'
  ),

  // --- Merchant Center ---
  list_sub_accounts: inspectSource('Listed sub-accounts', 'Listing sub-accounts', 'Merchant Center sub-accounts'),
  list_merchant_center_fields: inspectSource(
    'Listed Merchant Center fields',
    'Listing Merchant Center fields',
    'Merchant Center field catalog'
  ),
  list_merchant_center_dimension_values: inspectSource(
    'Listed Merchant Center values',
    'Listing Merchant Center values',
    'Merchant Center dimension values'
  ),

  // --- Alvie Data (Cirrus-backed; never say "Cirrus" in user-facing copy) ---
  // Catalog discovery is agent plumbing — hide it; fields/values/fetch stay visible.
  list_cirrus_reports: HIDDEN,
  list_cirrus_metrics_dimensions: inspectSource(
    'Listed Alvie Data fields',
    'Listing Alvie Data fields',
    'Alvie Data metrics and dimensions',
    resolveCirrusInspectDescription,
    resolveCirrusInspectTitles
  ),
  list_cirrus_dimension_values: inspectSource(
    'Listed Alvie Data filter values',
    'Listing Alvie Data filter values',
    'Alvie Data filter values',
    resolveCirrusInspectDescription,
    resolveCirrusInspectTitles
  ),

  // --- BigQuery ---
  list_bigquery_datasets: inspectSource('Listed BigQuery datasets', 'Listing BigQuery datasets', 'BigQuery datasets'),
  list_bigquery_tables: inspectSource('Listed BigQuery tables', 'Listing BigQuery tables', 'BigQuery tables'),
  get_bigquery_table_schema: inspectSource('Checked BigQuery schema', 'Checking BigQuery schema', 'table schema'),
  list_bigquery_column_values: inspectSource('Listed column values', 'Listing column values', 'BigQuery column values'),

  search_for_information_in_source_of_knowledge: dataSearch(
    'Searched knowledge base',
    'Searching knowledge base',
    (toolCall) => parseQueryArg(toolCall),
    (_toolCall, toolResultContent) => {
      const pages = groupKnowledgeSearchSources(toolResultContent)
      return pages.length > 0 ? pages : undefined
    }
  ),
  // Legacy / alternate name — AG-UI usually emits SearchAgent for web search.
  google_search: dataSearch('Searched the web', 'Searching the web', (toolCall) => parseQueryArg(toolCall)),
  // WebSearchAgentTool surfaces as the nested agent name with a `request` arg.
  SearchAgent: dataSearch('Searched the web', 'Searching the web', (toolCall) => parseQueryArg(toolCall, 'request')),
  run_google_ads_query: dataFetch('Google Ads report'),
  run_ga4_report: dataFetch('GA4 report'),
  run_ga4_funnel_report: dataFetch('funnel report'),
  run_search_analytics_query: dataFetch('Search Console report'),
  run_cross_channel_report: dataFetch('cross-channel report'),
  run_all_marketing_data_report: dataFetch('marketing report'),
  get_ga4_change_history: dataFetch('GA4 change history'),
  run_meta_ads_report: dataFetch('Meta Ads report'),
  run_linkedin_ads_report: dataFetch('LinkedIn Ads report'),
  run_tiktok_ads_report: dataFetch('TikTok Ads report'),
  run_snapchat_ads_report: dataFetch('Snapchat Ads report'),
  run_pinterest_ads_report: dataFetch('Pinterest Ads report'),
  run_merchant_center_query: dataFetch('Merchant Center report'),
  run_cirrus_report: {
    presentation: {
      kind: 'action_data_fetch',
      ...resolveCirrusFetchLabels(undefined),
      resolveFromArgs: resolveCirrusFetchLabels
    }
  },
  run_bigquery_query: dataFetch('BigQuery report'),
  transform_data: dataAnalysis('dataset'),
  create_report: dataAnalysis('dataset report'),
  detect_anomalies: dataAnalysis('dataset for anomalies'),
  analyze_single_series_causal_impact: dataAnalysis('dataset for causal impact'),
  analyze_diff_in_diff_causal_impact: dataAnalysis('dataset for diff-in-diff'),
  forecast_time_series: dataAnalysis('dataset for forecast'),
  forecast_ratio_metric: dataAnalysis('dataset for ratio forecast'),
  analyze_ad_fatigue: dataAnalysis('ads for creative fatigue'),
  run_skill_script: dataAnalysis('dataset with skill'),
  submit_plan_for_approval: {
    presentation: {
      kind: 'visible',
      title: 'Suggested plan',
      visibleRenderKind: 'submit_plan_for_approval',
      placement: 'message_end'
    }
  },
  suggest_data_source: {
    presentation: {
      kind: 'visible',
      title: 'Suggested data source',
      visibleRenderKind: 'suggest_data_source',
      placement: 'message_end'
    }
  },

  // --- Intentionally hidden (silent in Alvie; do not warn) ---
  check_data_source: HIDDEN,
  transfer_to_agent: HIDDEN,
  submit_instructions_update: HIDDEN,
  apply_instructions_update: HIDDEN,
  load_supporting_documents: HIDDEN,
  ingest_uploaded_csv: HIDDEN,
  create_chart: HIDDEN,
  create_data_chart: HIDDEN,
  CustomInstructionsAgent: HIDDEN,
  DataAuditAgent: HIDDEN,
  GA4KnowledgeAgent: HIDDEN,
  GSCKnowledgeAgent: HIDDEN,
  MetaKnowledgeAgent: HIDDEN,
  GoogleAdsKnowledgeAgent: HIDDEN,
  LinkedInKnowledgeAgent: HIDDEN,
  TikTokKnowledgeAgent: HIDDEN,
  SnapchatKnowledgeAgent: HIDDEN,
  PinterestKnowledgeAgent: HIDDEN,
  MerchantCenterKnowledgeAgent: HIDDEN,
  StarterReportKnowledgeAgent: HIDDEN,
  list_source_reports: HIDDEN,
  get_dimension_filter_hints: HIDDEN,
  get_metric_filter_hints: HIDDEN,
  get_order_bys_hints: HIDDEN
}
